import React, { useState, useEffect, useCallback, useRef } from "react";
import { usePermissions } from "./PermissionsContext";
import "./Main.css";

const API_BASE = "https://api.seritonex.com";
const getOrgId = () => {
  const raw = sessionStorage.getItem("organizationId");
  const parsed = raw ? Number(raw) : null;
  return parsed && !isNaN(parsed) ? parsed : null;
};

const getUserId = () => {
  const raw = sessionStorage.getItem("userId");
  const parsed = raw ? Number(raw) : null;
  return parsed && !isNaN(parsed) ? parsed : null;
};

const authHeaders = () => {
  const token = localStorage.getItem("token") || "";
  return {
    "Content-Type": "application/json",
    accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

async function apiFetch(method, path, body = null) {
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: authHeaders(),
      ...(body !== null ? { body: JSON.stringify(body) } : {}),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (e) {
    return null;
  }
}

const buildCurrentAllocationSummaryPayload = (overrides = {}) => {
  const now = new Date();
  const startDate = new Date(now);
  startDate.setHours(0, 0, 0, 0);
  const endDate = new Date(now);
  endDate.setHours(23, 59, 59, 999);

  const body = { is_supplier: false };

  Object.entries(overrides).forEach(([key, value]) => {
    if (
      (key === "branch_id" || key === "warehouse_id") &&
      Number(value) === 0
    ) {
      return;
    }
    if (value != null && value !== "") {
      body[key] = value;
    }
  });

  return body;
};

const normalizeInventoryResponse = (res) => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  return (
    res.summary ||
    res.data ||
    res.result ||
    res.allocations ||
    res.assets ||
    res.items ||
    []
  );
};

const normalizeBreakdownResponse = (res) => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  return (
    res.data ||
    res.summary ||
    res.allocations ||
    res.breakdown ||
    res.items ||
    res.assets ||
    []
  );
};

const API = {
  createAssetType: (data) =>
    apiFetch("POST", "/api/v1/amis/asset/create-type", data),
  updateAssetType: (data) =>
    apiFetch("POST", "/api/v1/amis/asset/update-type", data),
  deleteAssetType: (data) =>
    apiFetch("POST", "/api/v1/amis/asset/delete-type", data),
  fetchAllAssetTypes: (data) =>
    apiFetch("POST", "/api/v1/amis/asset/types", data),
  fetchProperties: () => apiFetch("POST", "/api/v1/amis/asset/properties", {}),
  createAsset: (data) => apiFetch("POST", "/api/v1/amis/asset/create", data),
  updateAsset: (data) => apiFetch("POST", "/api/v1/amis/asset/update", data),
  deleteAsset: (data) => apiFetch("POST", "/api/v1/amis/asset/delete", data),
  fetchAssetList: (data) => apiFetch("POST", "/api/v1/amis/asset/list", data),
  fetchCurrentAssetAllocationSummary: (data) =>
    apiFetch("POST", "/api/v1/amis/asset/current-allocation-summary", data),
  fetchCurrentAllocationBreakdown: (data) =>
    apiFetch("POST", "/api/v1/amis/asset/current-allocation-breakdown", data),
  fetchAssetDetails: (data) => apiFetch("POST", "/api/v1/amis/asset/get", data),
  fetchByBranch: (data) =>
    apiFetch("POST", "/api/v1/amis/asset/by-branch", data),
  fetchByWarehouse: (data) =>
    apiFetch("POST", "/api/v1/amis/asset/by-warehouse", data),
  fetchByAssetType: (data) =>
    apiFetch("POST", "/api/v1/amis/asset/by-type", data),
  getBranchesByUser: (user_id) =>
    apiFetch("POST", "/api/v1/amis/branch/by-user-id", { user_id }),
  getWarehousesByUser: (user_id) =>
    apiFetch("POST", "/api/v1/amis/warehouse/by-user", { user_id }),
  getBranches: () => apiFetch("GET", "/api/v1/amis/branch/get-all"),
};

// ─── CSV Export ───────────────────────────────────────────────────────────────
function downloadCSV(filename, rows, columns) {
  const header = columns.map((c) => c.label).join(",");
  const body = rows
    .map((row) =>
      columns
        .map((c) => {
          const val = row[c.key] ?? "";
          const str = String(val).replace(/"/g, '""');
          return /[,"\n]/.test(str) ? `"${str}"` : str;
        })
        .join(","),
    )
    .join("\n");
  const blob = new Blob([`${header}\n${body}`], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  Object.assign(document.createElement("a"), {
    href: url,
    download: filename,
  }).click();
  URL.revokeObjectURL(url);
}

const ASSETS_BREAKDOWN_COLS = [
  { label: "Serial Number", key: "serial_number" },
  { label: "Code", key: "asset_type_code" },
  { label: "Description", key: "asset_description" },
  { label: "Warehouse", key: "warehouse_name" },
  { label: "Organization", key: "organization_name" },
  { label: "Date In", key: "date_in" },
  { label: "Days", key: "allocated_days" },
];

// ─── Toast ─────────────────────────────────────────────────────────────────────
function Toast({ toasts, remove }) {
  return (
    <div className="toast-wrap">
      {toasts.map((t) => (
        <div key={t.id} className={`toast toast-${t.type}`}>
          <span style={{ fontSize: 16 }}>
            {t.type === "success"
              ? "✓"
              : t.type === "danger"
                ? "✕"
                : t.type === "warning"
                  ? "⚠"
                  : "ℹ"}
          </span>
          <span style={{ flex: 1 }}>{t.msg}</span>
          <button
            onClick={() => remove(t.id)}
            style={{
              background: "none",
              border: "none",
              color: "var(--text3)",
              cursor: "pointer",
              fontSize: 14,
            }}
          >
            <i className="bi bi-x-lg" style={{ marginLeft: 5.5 }}></i>
          </button>
        </div>
      ))}
    </div>
  );
}

function useToast() {
  const [toasts, setToasts] = useState([]);
  const show = useCallback((msg, type = "info") => {
    const id = Date.now();
    setToasts((p) => [...p, { id, msg, type }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 4000);
  }, []);
  const remove = useCallback(
    (id) => setToasts((p) => p.filter((t) => t.id !== id)),
    [],
  );
  return { toasts, show, remove };
}

// ─── Confirm Modal ─────────────────────────────────────────────────────────────
function ConfirmModal({ title, msg, onConfirm, onCancel, danger }) {
  return (
    <div className="overlay" onClick={onCancel}>
      <div
        className="modal"
        style={{ maxWidth: 420 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mhdr">
          <span className="mtitle">{title}</span>
          <button
            className="btn btn-g btn-sm btn-ico btn-pill"
            onClick={onCancel}
          >
            <i className="bi bi-x-lg" style={{ marginLeft: 5.5 }}></i>
          </button>
        </div>
        <div className="mbody">
          <p style={{ fontSize: 13, color: "var(--text2)", lineHeight: 1.6 }}>
            {msg}
          </p>
        </div>
        <div className="mfoot">
          <button className="btn btn-s btn-pill" onClick={onCancel}>
            Cancel
          </button>
          <button
            className={`btn ${danger ? "btn-d" : "btn-p"}`}
            onClick={onConfirm}
          >
            {danger ? "Delete" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Asset Type Modal ──────────────────────────────────────────────────────────
function AssetTypeModal({ mode, initial, properties, onClose, onSave }) {
  const [form, setForm] = useState(
    initial || {
      asset_type_id: "",
      type_code: "",
      description: "",
      organization_id: "",
      asset_type_properties: [
        {
          link_id: null,
          is_linked: null,
          prop_value: null,
          property_id: null,
          prop_description: null,
          asset_type_property_id: null,
        },
      ],
    },
  );
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addProp = () =>
    setForm((f) => {
      const newProp = {
        property_id: "",
        prop_description: "",
        is_linked: false,
        link_id: "0",
        prop_value: "",
      };
      return { ...f, properties: [...(f.properties || []), newProp] };
    });

  const setProp = (i, k, v) =>
    setForm((f) => {
      const props = [...(f.properties || [])];
      props[i] = { ...props[i], [k]: v };
      return { ...f, properties: props };
    });

  const removeProp = (i) =>
    setForm((f) => ({
      ...f,
      properties: (f.properties || []).filter((_, idx) => idx !== i),
    }));

  const handleSave = async () => {
    if (!form.type_code.trim()) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="mhdr">
          <span className="mtitle">
            {mode === "create" ? "Create Asset Type" : "Edit Asset Type"}
          </span>
          <button
            className="btn btn-g btn-sm btn-ico btn-pill"
            onClick={onClose}
          >
            <i className="bi bi-x-lg" style={{ marginLeft: 5.5 }}></i>
          </button>
        </div>
        <div className="mbody">
          <div className="frow mb12">
            <div className="fg">
              <label className="fl">Type Code *</label>
              <input
                className="fi"
                value={form.type_code}
                onChange={(e) => set("type_code", e.target.value)}
                placeholder="e.g. LAPTOP"
              />
            </div>
            <div className="fg">
              <label className="fl">Organization ID</label>
              <input
                className="fi"
                value={form.organization_id}
                onChange={(e) => set("organization_id", e.target.value)}
                placeholder="Org ID"
              />
            </div>
          </div>
          <div className="fg mb12">
            <label className="fl">Description</label>
            <input
              className="fi"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Brief description"
            />
          </div>
          <div className="fb mb8">
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--text3)",
                textTransform: "uppercase",
                letterSpacing: ".1em",
              }}
            >
              Properties
            </span>
            <button className="btn btn-s btn-sm btn-pill" onClick={addProp}>
              + Add Property
            </button>
          </div>
          {(form.properties || []).length === 0 && (
            <div className="empty-branch mb12">
              <div style={{ fontSize: 12, color: "var(--text3)" }}>
                No properties yet. Add one above.
              </div>
            </div>
          )}
          {(form.properties || []).map((p, i) => (
            <div key={i} className="product-line-row mb8">
              <div className="product-line-cell" style={{ flex: "2 1 140px" }}>
                <label className="fl" style={{ fontSize: 9 }}>
                  Property ID
                </label>
                <input
                  className="fi"
                  value={p.property_id}
                  onChange={(e) => setProp(i, "property_id", e.target.value)}
                  placeholder="ID"
                />
              </div>
              <div className="product-line-cell" style={{ flex: "3 1 180px" }}>
                <label className="fl" style={{ fontSize: 9 }}>
                  Description
                </label>
                <input
                  className="fi"
                  value={p.prop_description}
                  onChange={(e) =>
                    setProp(i, "prop_description", e.target.value)
                  }
                  placeholder="Prop description"
                />
              </div>
              <div className="product-line-cell" style={{ flex: "2 1 120px" }}>
                <label className="fl" style={{ fontSize: 9 }}>
                  Value
                </label>
                <input
                  className="fi"
                  value={p.prop_value}
                  onChange={(e) => setProp(i, "prop_value", e.target.value)}
                  placeholder="Value"
                />
              </div>
              <div className="product-line-cell product-line-faulty">
                <label className="fl" style={{ fontSize: 9 }}>
                  Linked
                </label>
                <button
                  type="button"
                  className={`faulty-toggle${p.is_linked ? " on" : ""}`}
                  onClick={() => setProp(i, "is_linked", !p.is_linked)}
                >
                  <div className={`faulty-pill${p.is_linked ? " on" : ""}`}>
                    <div className="faulty-knob" />
                  </div>
                  <span className="faulty-label">
                    {p.is_linked ? "Yes" : "No"}
                  </span>
                </button>
              </div>
              {p.is_linked && (
                <div
                  className="product-line-cell"
                  style={{ flex: "2 1 100px" }}
                >
                  <label className="fl" style={{ fontSize: 9 }}>
                    Link ID
                  </label>
                  <input
                    className="fi"
                    value={p.link_id}
                    onChange={(e) => setProp(i, "link_id", e.target.value)}
                    placeholder="Link ID"
                  />
                </div>
              )}
              <div className="product-line-cell product-line-remove">
                <label className="fl" style={{ fontSize: 9 }}>
                  &nbsp;
                </label>
                <button
                  className="btn btn-d btn-sm product-remove-btn btn-pill"
                  onClick={() => removeProp(i)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="mfoot">
          <button className="btn btn-s btn-pill" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-p btn-pill"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spin-icon" /> Saving…
              </>
            ) : mode === "create" ? (
              "Create Type"
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Asset Modal ───────────────────────────────────────────────────────────────
function AssetModal({
  mode,
  initial,
  assetTypes,
  properties,
  onClose,
  onSave,
}) {
  const [form, setForm] = useState(
    initial || {
      serial_number: "",
      asset_type_id: "",
      asset_description: "",
      organization_id: "",
      properties: [],
    },
  );
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addProp = () =>
    setForm((f) => ({
      ...f,
      properties: [
        ...f.properties,
        { property_id: "", prop_description: "", prop_value: "" },
      ],
    }));

  const setProp = (i, k, v) =>
    setForm((f) => {
      const props = [...f.properties];
      props[i] = { ...props[i], [k]: v };
      return { ...f, properties: props };
    });

  const removeProp = (i) =>
    setForm((f) => ({
      ...f,
      properties: f.properties.filter((_, idx) => idx !== i),
    }));

  const handleSave = async () => {
    if (!form.serial_number.trim()) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="mhdr">
          <span className="mtitle">
            {mode === "create" ? "Register New Asset" : "Edit Asset"}
          </span>
          <button
            className="btn btn-g btn-sm btn-ico btn-pill"
            onClick={onClose}
          >
            <i className="bi bi-x-lg" style={{ marginLeft: 5.5 }}></i>
          </button>
        </div>
        <div className="mbody">
          <div className="frow mb12">
            <div className="fg">
              <label className="fl">Serial Number *</label>
              <input
                className="fi"
                value={form.serial_number}
                onChange={(e) => set("serial_number", e.target.value)}
                placeholder="e.g. SN-20240001"
              />
            </div>
            <div className="fg">
              <label className="fl">Asset Type</label>
              <select
                className="fi fsel"
                value={form.asset_type_id}
                onChange={(e) => set("asset_type_id", e.target.value)}
              >
                <option value="">— Select type —</option>
                {assetTypes.map((t) => (
                  <option
                    key={t.asset_type_id || t.id}
                    value={t.asset_type_id || t.id}
                  >
                    {t.type_code || t.description || t.asset_type_id}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="frow mb12">
            <div className="fg">
              <label className="fl">Organization ID</label>
              <input
                className="fi"
                value={form.organization_id}
                onChange={(e) => set("organization_id", e.target.value)}
                placeholder="Org ID"
              />
            </div>
            <div className="fg">
              <label className="fl">Description</label>
              <input
                className="fi"
                value={form.asset_description}
                onChange={(e) => set("asset_description", e.target.value)}
                placeholder="Brief description"
              />
            </div>
          </div>
          <div className="fb mb8">
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--text3)",
                textTransform: "uppercase",
                letterSpacing: ".1em",
              }}
            >
              Properties
            </span>
            <button className="btn btn-s btn-sm btn-pill" onClick={addProp}>
              + Add Property
            </button>
          </div>
          {form.properties.length === 0 && (
            <div className="empty-branch mb12">
              <div style={{ fontSize: 12, color: "var(--text3)" }}>
                No properties. Add one above.
              </div>
            </div>
          )}
          {form.properties.map((p, i) => (
            <div key={i} className="product-line-row mb8">
              <div className="product-line-cell" style={{ flex: "2 1 120px" }}>
                <label className="fl" style={{ fontSize: 9 }}>
                  Property ID
                </label>
                <input
                  className="fi"
                  value={p.property_id}
                  onChange={(e) => setProp(i, "property_id", e.target.value)}
                  placeholder="ID"
                />
              </div>
              <div className="product-line-cell" style={{ flex: "3 1 160px" }}>
                <label className="fl" style={{ fontSize: 9 }}>
                  Description
                </label>
                <input
                  className="fi"
                  value={p.prop_description}
                  onChange={(e) =>
                    setProp(i, "prop_description", e.target.value)
                  }
                  placeholder="Label"
                />
              </div>
              <div className="product-line-cell" style={{ flex: "3 1 160px" }}>
                <label className="fl" style={{ fontSize: 9 }}>
                  Value
                </label>
                <input
                  className="fi"
                  value={p.prop_value}
                  onChange={(e) => setProp(i, "prop_value", e.target.value)}
                  placeholder="Value"
                />
              </div>
              <div className="product-line-cell product-line-remove">
                <label className="fl" style={{ fontSize: 9 }}>
                  &nbsp;
                </label>
                <button
                  className="btn btn-d btn-sm product-remove-btn btn-pill"
                  onClick={() => removeProp(i)}
                >
                  <i className="bi bi-x-lg" style={{ marginLeft: 1 }}></i>
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="mfoot">
          <button className="btn btn-s btn-pill" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Asset Detail Modal ────────────────────────────────────────────────────────
function AssetDetailModal({
  asset,
  onClose,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
}) {
  if (!asset) return null;
  const props = asset.properties || [];
  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="mhdr">
          <div style={{ flex: 1 }}>
            <div className="mtitle">
              {asset.serial_number || "Asset Details"}
            </div>
            <div style={{ fontSize: 11, color: "var(--text3)" }}>
              ID: {asset.asset_id || asset.id || "—"}
            </div>
          </div>
          {canEdit && (
            <button
              className="btn btn-s btn-sm btn-pill"
              onClick={() => {
                onClose();
                onEdit(asset);
              }}
            >
              Edit
            </button>
          )}
          {canDelete && (
            <button
              className="btn btn-d btn-sm btn-pill"
              onClick={() => {
                onClose();
                onDelete(asset);
              }}
            >
              <i className="bi bi-trash"></i>
            </button>
          )}
          <button
            className="btn btn-g btn-sm btn-ico btn-pill"
            onClick={onClose}
          >
            <i className="bi bi-x-lg" style={{ marginLeft: 5.5 }}></i>
          </button>
        </div>
        <div className="mbody">
          <div className="review-block mb16">
            <div className="review-row">
              <span className="review-label">Serial #</span>
              <span className="review-value">{asset.serial_number || "—"}</span>
            </div>
            <div className="review-row">
              <span className="review-label">Type ID</span>
              <span className="review-value">{asset.asset_type_id || "—"}</span>
            </div>
            <div className="review-row">
              <span className="review-label">Description</span>
              <span className="review-value">
                {asset.asset_description || "—"}
              </span>
            </div>
            <div className="review-row">
              <span className="review-label">Org ID</span>
              <span className="review-value">
                {asset.organization_id || "—"}
              </span>
            </div>
          </div>
          {props.length > 0 && (
            <>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "var(--text3)",
                  textTransform: "uppercase",
                  letterSpacing: ".1em",
                  marginBottom: 8,
                }}
              >
                Properties
              </div>
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Property ID</th>
                      <th>Description</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {props.map((p, i) => (
                      <tr key={i}>
                        <td className="mono">{p.property_id}</td>
                        <td>{p.prop_description}</td>
                        <td>{p.prop_value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        <div className="mfoot">
          <button className="btn btn-s btn-pill" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Shared Allocation Table ───────────────────────────────────────────────────
function AllocationTable({ rows, loading, search, emptyMsg }) {
  const filtered = rows.filter((a) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (a.asset_type_code || "").toLowerCase().includes(s) ||
      (a.asset_description || "").toLowerCase().includes(s) ||
      (a.warehouse_name || "").toLowerCase().includes(s) ||
      (a.branch_name || "").toLowerCase().includes(s) ||
      (a.organization_name || "").toLowerCase().includes(s) ||
      String(a.quantity || "").includes(s)
    );
  });

  const totalQty = filtered.reduce((s, r) => s + Number(r.quantity || 0), 0);

  return (
    <div className="tbl-wrap">
      <table className="tbl">
        <colgroup>
          <col className="col-code" />
          <col className="col-description" />
          <col className="col-warehouse" />
          <col className="col-branch" />
          <col className="col-organization" />
          <col className="col-qty" />
        </colgroup>
        <thead>
          <tr>
            <th>Code</th>
            <th>Description</th>
            <th>Warehouse</th>
            <th>Branch</th>
            <th>Organization</th>
            <th className="numeric-header" style={{ textAlign: "right" }}>
              Qty
            </th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td
                colSpan={6}
                style={{
                  textAlign: "center",
                  padding: 32,
                  color: "var(--text3)",
                }}
              >
                <span className="spin-icon" style={{ marginRight: 8 }} />
                Loading…
              </td>
            </tr>
          )}
          {!loading && filtered.length === 0 && (
            <tr>
              <td colSpan={6}>
                <div className="empty">
                  <div className="empty-ico"></div>
                  <div>
                    {emptyMsg ||
                      (search
                        ? `Not Found "${search}"`
                        : "Not Found")}
                  </div>
                </div>
              </td>
            </tr>
          )}
          {!loading &&
            filtered.map((a, idx) => (
              <tr
                key={`${a.asset_type_code || "asset"}-${a.warehouse_name || "wh"}-${a.branch_name || "br"}-${idx}`}
              >
                <td>{a.asset_type_code || "—"}</td>
                <td className="asset-description-cell">
                  {a.asset_description || "—"}
                </td>
                <td>{a.warehouse_name || "—"}</td>
                <td>{a.branch_name || "—"}</td>
                <td>{a.organization_name || "—"}</td>
                <td
                  className="mono numeric-cell"
                  style={{ textAlign: "right" }}
                >
                  {Number(a.quantity || 0).toLocaleString()}
                </td>
              </tr>
            ))}
        </tbody>
        {filtered.length > 0 && !loading && (
          <tfoot>
            <tr>
              <td
                colSpan={5}
                style={{
                  textAlign: "right",
                  fontWeight: 700,
                  padding: "10px 16px",
                }}
              >
                Totals
              </td>
              <td
                className="mono"
                style={{
                  textAlign: "right",
                  fontWeight: 700,
                  padding: "10px 16px",
                }}
              >
                {totalQty.toLocaleString()}
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

// ─── Breakdown Table ───────────────────────────────────────────────────────────
function BreakdownTable({ rows, loading, search }) {
  const filtered = rows.filter((a) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (a.serial_number || "").toLowerCase().includes(s) ||
      (a.asset_type_code || "").toLowerCase().includes(s) ||
      (a.asset_description || "").toLowerCase().includes(s) ||
      (a.warehouse_name || "").toLowerCase().includes(s) ||
      (a.branch_name || "").toLowerCase().includes(s) ||
      (a.organization_name || "").toLowerCase().includes(s)
    );
  });

  const fmt = (dt) => {
    if (!dt) return "—";
    try {
      const d = new Date(dt.replace(" ", "T"));
      return d.toLocaleString("en-ZA", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    } catch {
      return dt;
    }
  };

  return (
    <div className="tbl-wrap">
      <table className="tbl">
        <thead>
          <tr>
            <th>Serial #</th>
            <th>Code</th>
            <th>Description</th>
            <th>Warehouse</th>
            <th>Organization</th>
            <th>Date In</th>
            <th></th>
            <th className="numeric-header">Days</th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td
                colSpan={8}
                style={{
                  textAlign: "center",
                  padding: 32,
                  color: "var(--text3)",
                }}
              >
                <span className="spin-icon" style={{ marginRight: 8 }} />
                Loading…
              </td>
            </tr>
          )}
          {!loading && filtered.length === 0 && (
            <tr>
              <td colSpan={8}>
                <div className="empty">
                  <div className="empty-ico"></div>
                  <div>
                    {search ? `No assets match "${search}"` : "Not Found"}
                  </div>
                </div>
              </td>
            </tr>
          )}
          {!loading &&
            filtered.map((a, idx) => (
              <tr key={a.warehouse_stock_id || idx}>
                <td className="mono">{a.serial_number || "—"}</td>
                <td>{a.asset_type_code || "—"}</td>
                <td className="asset-description-cell">
                  {a.asset_description || "—"}
                </td>
                <td>{a.warehouse_name || "—"}</td>
                <td>{a.organization_name || "—"}</td>
                <td className="mono" style={{ fontSize: 11 }}>
                  {fmt(a.date_in)}
                </td>
                <td></td>
                <td className="mono numeric-cell">{a.allocated_days ?? "—"}</td>
              </tr>
            ))}
        </tbody>
        {filtered.length > 0 && !loading && (
          <tfoot>
            <tr>
              <td
                colSpan={7}
                style={{
                  textAlign: "right",
                  fontWeight: 700,
                  padding: "10px 0px",
                }}
              >
                Total Assets
              </td>
              <td
                className="mono"
                style={{
                  textAlign: "left",
                  fontWeight: 700,
                  padding: "10px 16px",
                }}
              >
                {filtered.length}
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

// ─── Filtered Result Panel ─────────────────────────────────────────────────────
function FilteredResultPanel({
  title,
  icon,
  badges,
  rows,
  loading,
  search,
  showBreakdown,
}) {
  const subtitle =
    !loading && badges.length > 0
      ? badges.map((b) => b.label).join(" · ")
      : loading
        ? "Loading…"
        : "";

  return (
    <div
      className="panel"
      style={{ borderRadius: "var(--r12, 12px)", overflow: "hidden" }}
    >
      <div className="ph">
        <div>
          <div
            className="ph-title"
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            <i
              className={`bi ${icon}`}
              style={{ color: "var(--text3)", fontSize: 14 }}
            />
            {title}
          </div>
          {subtitle && <div className="ph-sub">{subtitle}</div>}
        </div>
      </div>
      <div className="pb" style={{ padding: 0 }}>
        {showBreakdown ? (
          <BreakdownTable rows={rows} loading={loading} search={search} />
        ) : (
          <AllocationTable rows={rows} loading={loading} search={search} />
        )}
      </div>
    </div>
  );
}

// ─── Assets Tab ────────────────────────────────────────────────────────────────
function AssetsTab({ toast, assetTypes, properties }) {
  const { canCreateAssets, canUpdateAssets, canDeleteAssets } =
    usePermissions();

  const [assets, setAssets] = useState([]);
  const [loadingMain, setLoadingMain] = useState(false);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const [branches, setBranches] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(0);
  const [selectedWarehouse, setSelectedWarehouse] = useState(0);

  const [filteredRows, setFilteredRows] = useState([]);
  const [loadingFiltered, setLoadingFiltered] = useState(false);

  const [showBreakdown, setShowBreakdown] = useState(false);
  const [breakdownRows, setBreakdownRows] = useState([]);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownError, setBreakdownError] = useState(null);

  // ── Load branches ─────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const userId = getUserId();
      let res = null;
      if (userId) res = await API.getBranchesByUser(userId);
      if (!res) res = await API.getBranches();
      if (!cancelled && res) {
        const list = Array.isArray(res)
          ? res
          : res.branches || res.data || res.items || [];
        setBranches(Array.isArray(list) ? list : []);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Load warehouses (re-fetch when branch changes) ────────────────────────────
  useEffect(() => {
    const userId = getUserId();
    if (!userId) {
      setWarehouses([]);
      setSelectedWarehouse(0);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const raw = await API.getWarehousesByUser(userId);
        let list = [];
        if (Array.isArray(raw)) list = raw;
        else if (Array.isArray(raw?.warehouses)) list = raw.warehouses;
        else if (Array.isArray(raw?.data)) list = raw.data;
        else if (
          raw &&
          typeof raw === "object" &&
          (raw.warehouse_id != null || raw.id != null)
        )
          list = [raw];
        if (!cancelled) setWarehouses(list);
      } catch {
        if (!cancelled) setWarehouses([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedBranch]);

  // ── Load main (unfiltered) summary ────────────────────────────────────────────
  const loadMain = useCallback(async () => {
    setLoadingMain(true);
    const orgId = getOrgId();
    const payload = buildCurrentAllocationSummaryPayload({
      ...(orgId !== null ? { organization_id: orgId } : {}),
    });
    const res = await API.fetchCurrentAssetAllocationSummary(payload);
    setLoadingMain(false);
    if (res) {
      setAssets(normalizeInventoryResponse(res));
    } else {
      toast("Failed to load assets", "danger");
    }
  }, [toast]);

  // ── Breakdown ─────────────────────────────────────────────────────────────────
  const loadBreakdown = useCallback(async () => {
    setBreakdownLoading(true);
    setBreakdownError(null);
    try {
      const orgId = getOrgId();
      const payload = {
        is_supplier: false,
        ...(orgId !== null ? { organization_id: orgId } : {}),
        ...(selectedBranch > 0 ? { branch_id: selectedBranch } : {}),
        ...(selectedWarehouse > 0 ? { warehouse_id: selectedWarehouse } : {}),
      };
      const res = await API.fetchCurrentAllocationBreakdown(payload);
      setBreakdownRows(res ? normalizeBreakdownResponse(res) : []);
    } catch (err) {
      setBreakdownError(err?.message || "Failed to load breakdown");
      setBreakdownRows([]);
    } finally {
      setBreakdownLoading(false);
    }
  }, [selectedBranch, selectedWarehouse]);

  useEffect(() => {
    loadMain();
  }, [loadMain]);

  useEffect(() => {
    if (showBreakdown) loadBreakdown();
  }, [showBreakdown, loadBreakdown]);

  // ── Filtered summary ──────────────────────────────────────────────────────────
  useEffect(() => {
    const run = async () => {
      if (selectedWarehouse <= 0 && selectedBranch <= 0) {
        setFilteredRows([]);
        return;
      }
      setLoadingFiltered(true);
      setFilteredRows([]);
      const orgId = getOrgId();
      const payload = buildCurrentAllocationSummaryPayload({
        ...(orgId !== null ? { organization_id: orgId } : {}),
        ...(selectedBranch > 0 ? { branch_id: selectedBranch } : {}),
        ...(selectedWarehouse > 0 ? { warehouse_id: selectedWarehouse } : {}),
      });
      const res = await API.fetchCurrentAssetAllocationSummary(payload);
      setLoadingFiltered(false);
      setFilteredRows(res ? normalizeInventoryResponse(res) : []);
    };
    run();
  }, [selectedBranch, selectedWarehouse]);

  // ── Derived display values ─────────────────────────────────────────────────────
  const hasActiveFilter = selectedBranch > 0 || selectedWarehouse > 0;

  const filteredTitle = (() => {
    if (selectedWarehouse > 0) {
      const w = warehouses.find(
        (w) => (w.warehouse_id ?? w.id) === selectedWarehouse,
      );
      return w
        ? (w.warehouse_name ??
            w.name ??
            w.display_name ??
            `Warehouse ${selectedWarehouse}`)
        : `Warehouse ${selectedWarehouse}`;
    }
    if (selectedBranch > 0) {
      const b = branches.find((b) => (b.branch_id ?? b.id) === selectedBranch);
      return b
        ? (b.branch_name ??
            b.name ??
            b.display_name ??
            `Branch ${selectedBranch}`)
        : `Branch ${selectedBranch}`;
    }
    return "";
  })();

  const filteredIcon = selectedWarehouse > 0 ? "bi-building" : "bi-diagram-3";

  const filteredSummaryBadges = !loadingFiltered
    ? (() => {
        const totalQty = filteredRows.reduce(
          (s, r) => s + Number(r.quantity || 0),
          0,
        );
        return [
          {
            cls: "badge-blue",
            label: `${filteredRows.length} line${filteredRows.length !== 1 ? "s" : ""}`,
          },
          { cls: "badge-green", label: `${totalQty.toLocaleString()} qty` },
        ];
      })()
    : [];

  const filteredBreakdownBadges = !breakdownLoading
    ? [
        {
          cls: "badge-blue",
          label: `${breakdownRows.length} asset${breakdownRows.length !== 1 ? "s" : ""}`,
        },
      ]
    : [];

  const totalQty = assets.reduce((s, r) => s + Number(r.quantity || 0), 0);
  const summarySubtitle = loadingMain
    ? "Loading…"
    : `${assets.length} type${assets.length !== 1 ? "s" : ""} · ${totalQty.toLocaleString()} units`;
  const breakdownSubtitle = breakdownLoading
    ? "Loading…"
    : breakdownError
      ? "Error loading breakdown"
      : `${breakdownRows.length} asset${breakdownRows.length !== 1 ? "s" : ""}`;

  // ── Asset CRUD ────────────────────────────────────────────────────────────────
  const handleCreate = async (form) => {
    const res = await API.createAsset({
      ...form,
      asset_type_id: Number(form.asset_type_id) || 0,
      organization_id: Number(form.organization_id) || 0,
    });
    if (res) {
      toast("Asset registered", "success");
      setModal(null);
      loadMain();
    } else toast("Failed to create asset", "danger");
  };

  const handleUpdate = async (form) => {
    const res = await API.updateAsset({
      ...form,
      asset_id: form.asset_id || form.id,
      asset_type_id: Number(form.asset_type_id) || 0,
      organization_id: Number(form.organization_id) || 0,
    });
    if (res) {
      toast("Asset updated", "success");
      setModal(null);
      loadMain();
    } else toast("Failed to update asset", "danger");
  };

  const handleDelete = async (asset) => {
    const res = await API.deleteAsset({ asset_id: asset.asset_id || asset.id });
    if (res) {
      toast("Asset deleted", "success");
      setConfirm(null);
      loadMain();
    } else toast("Failed to delete asset", "danger");
  };

  return (
    <>
      {/* ── Toolbar ── */}
      <div className="fb mb22" style={{ flexWrap: "wrap", gap: 12 }}>
        {/* Left: search */}
        <div className="tsearch" style={{ flex: "1 1 280px", maxWidth: 520 }}>
          <i
            className="bi bi-search"
            style={{ color: "var(--text3)", fontSize: 13 }}
          ></i>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by code, description, warehouse, branch…"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "var(--text3)",
                fontSize: 13,
                padding: "0 4px",
                lineHeight: 1,
              }}
            >
              <i className="bi bi-x-lg" style={{ fontSize: 11 }}></i>
            </button>
          )}
        </div>

        {/* Right: branch/warehouse selects + action buttons */}
        <div className="rpt-topbar-actions">
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 11, color: "var(--tc3)" }}>Branch</span>
            <select
              value={selectedBranch}
              onChange={(e) => {
                setSelectedBranch(Number(e.target.value || 0));
                setSelectedWarehouse(0);
              }}
              className="tfilter"
              style={{ width: 180, fontSize: 12 }}
            >
              <option value={0}>All branches</option>
              {branches.map((b) => {
                const id = b.branch_id ?? b.id ?? 0;
                const name =
                  b.branch_name ?? b.name ?? b.display_name ?? `Branch ${id}`;
                return (
                  <option key={id} value={id}>
                    {name}
                  </option>
                );
              })}
            </select>
          </label>

          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 11, color: "var(--tc3)" }}>Warehouse</span>
            <select
              value={selectedWarehouse}
              onChange={(e) =>
                setSelectedWarehouse(Number(e.target.value || 0))
              }
              className="tfilter"
              style={{ width: 180, fontSize: 12 }}
            >
              <option value={0}>All warehouses</option>
              {warehouses.map((w) => {
                const id = w.warehouse_id ?? w.id ?? 0;
                const name =
                  w.warehouse_name ??
                  w.name ??
                  w.display_name ??
                  `Warehouse ${id}`;
                return (
                  <option key={id} value={id}>
                    {name}
                  </option>
                );
              })}
            </select>
          </label>

          <button
            className="btn btn-s"
            onClick={() => setShowBreakdown((prev) => !prev)}
            title={showBreakdown ? "Back to summary" : "Show full breakdown"}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <i
              className={`bi bi-${showBreakdown ? "list-ul" : "list-nested"}`}
            />
            <span style={{ marginLeft: 4 }}>
              {showBreakdown ? "Summary" : "Breakdown"}
            </span>
          </button>

          {/* CSV export — only visible in breakdown mode */}
          {showBreakdown && (
            <button
              className="btn btn-s"
              onClick={() =>
                downloadCSV(
                  `asset-breakdown-${new Date().toISOString().slice(0, 10)}.csv`,
                  breakdownRows,
                  ASSETS_BREAKDOWN_COLS,
                )
              }
              title="Export breakdown as CSV"
              style={{ display: "flex", alignItems: "center", gap: 5 }}
              disabled={breakdownLoading || breakdownRows.length === 0}
            >
              <i className="bi bi-download" style={{ fontSize: 12 }} />
              <span>CSV</span>
            </button>
          )}

          <button
            className="btn btn-s"
            onClick={() => (showBreakdown ? loadBreakdown() : loadMain())}
            style={{ display: "flex", alignItems: "center", gap: 5 }}
            disabled={loadingMain || breakdownLoading}
          >
            <i className="bi bi-arrow-clockwise" style={{ fontSize: 12 }}></i>
          </button>
        </div>
      </div>

      {/* ── Content panel ── */}
      {hasActiveFilter && filteredTitle ? (
        <FilteredResultPanel
          title={filteredTitle}
          icon={filteredIcon}
          badges={
            showBreakdown ? filteredBreakdownBadges : filteredSummaryBadges
          }
          rows={showBreakdown ? breakdownRows : filteredRows}
          loading={showBreakdown ? breakdownLoading : loadingFiltered}
          search={search}
          showBreakdown={showBreakdown}
        />
      ) : (
        <div
          className="panel"
          style={{ borderRadius: "var(--r12, 12px)", overflow: "hidden" }}
        >
          <div className="ph">
            <div>
              <div className="ph-title">
                {showBreakdown
                  ? "Current allocation breakdown"
                  : "Allocation summary"}
              </div>
              <div className="ph-sub">
                {showBreakdown ? breakdownSubtitle : summarySubtitle}
              </div>
            </div>
          </div>
          <div className="pb" style={{ padding: 0 }}>
            {showBreakdown ? (
              <BreakdownTable
                rows={breakdownRows}
                loading={breakdownLoading}
                search={search}
              />
            ) : (
              <AllocationTable
                rows={assets}
                loading={loadingMain}
                search={search}
              />
            )}
          </div>
        </div>
      )}

      {/* ── Modals ── */}
      {modal?.type === "create-asset" && canCreateAssets && (
        <AssetModal
          mode="create"
          assetTypes={assetTypes}
          properties={properties}
          onClose={() => setModal(null)}
          onSave={handleCreate}
        />
      )}
      {modal?.type === "edit-asset" && canUpdateAssets && (
        <AssetModal
          mode="edit"
          initial={modal.data}
          assetTypes={assetTypes}
          properties={properties}
          onClose={() => setModal(null)}
          onSave={handleUpdate}
        />
      )}
      {modal?.type === "detail" && (
        <AssetDetailModal
          asset={modal.data}
          onClose={() => setModal(null)}
          onEdit={(a) => setModal({ type: "edit-asset", data: a })}
          onDelete={(a) => setConfirm({ action: "delete-asset", data: a })}
          canEdit={canUpdateAssets}
          canDelete={canDeleteAssets}
        />
      )}
      {confirm?.action === "delete-asset" && canDeleteAssets && (
        <ConfirmModal
          title="Delete Asset"
          msg={`Permanently delete asset "${confirm.data.serial_number}"? This cannot be undone.`}
          danger
          onConfirm={() => handleDelete(confirm.data)}
          onCancel={() => setConfirm(null)}
        />
      )}
    </>
  );
}

// ─── Asset Types Tab ───────────────────────────────────────────────────────────
function AssetTypesTab({ toast, onTypesChange }) {
  const { canCreateAssets, canUpdateAssets, canDeleteAssets } =
    usePermissions();

  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const sessionOrgId = getOrgId();
    const payload = {};
    if (sessionOrgId !== null && !isNaN(sessionOrgId))
      payload.organization_id = sessionOrgId;
    const res = await API.fetchAllAssetTypes(payload);
    setLoading(false);
    if (res) {
      const raw = Array.isArray(res)
        ? res
        : res.assetTypes || res.types || res.asset_types || [];
      const list = raw.map((t) => ({
        ...t,
        properties: t.asset_type_properties || [],
      }));
      setTypes(list);
      onTypesChange(list);
    } else toast("Failed to load asset types", "danger");
  }, [toast, onTypesChange]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (form) => {
    const res = await API.createAssetType({
      ...form,
      organization_id: form.organization_id || "",
    });
    if (res) {
      toast("Asset type created", "success");
      setModal(null);
      load();
    } else toast("Failed to create asset type", "danger");
  };

  const handleUpdate = async (form) => {
    const res = await API.updateAssetType({
      ...form,
      asset_type_id: form.asset_type_id || form.id || 0,
    });
    if (res) {
      toast("Asset type updated", "success");
      setModal(null);
      load();
    } else toast("Failed to update asset type", "danger");
  };

  const handleDelete = async (t) => {
    const res = await API.deleteAssetType({
      asset_type_id: t.asset_type_id || t.id,
    });
    if (res) {
      toast("Asset type deleted", "success");
      setConfirm(null);
      load();
    } else toast("Failed to delete asset type", "danger");
  };

  const filtered = types.filter((t) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (t.type_code || "").toLowerCase().includes(s) ||
      (t.description || "").toLowerCase().includes(s)
    );
  });

  const showActionsCol = canUpdateAssets || canDeleteAssets;

  const subtitle = loading
    ? "Loading…"
    : `${filtered.length} type${filtered.length !== 1 ? "s" : ""}`;

  return (
    <>
      <div className="panel">
        <div className="ph">
          <div>
            <div className="ph-title">Asset types</div>
            <div className="ph-sub">{subtitle}</div>
          </div>
          <div className="rpt-topbar-actions">
            <input
              type="text"
              className="tfilter"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search types…"
              style={{ fontSize: 12, width: 160 }}
            />
            <button className="btn btn-s" onClick={load}>
              <i className="bi bi-arrow-clockwise" style={{ fontSize: 12 }}></i>{" "}
              Refresh
            </button>
            {canCreateAssets && (
              <button
                className="btn btn-p"
                onClick={() => setModal({ type: "create" })}
              >
                + New Type
              </button>
            )}
          </div>
        </div>

        <div className="pb" style={{ padding: 0 }}>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Type ID</th>
                  <th>Type Code</th>
                  <th>Description</th>
                  <th>Org ID</th>
                  <th>Properties</th>
                  {showActionsCol && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td
                      colSpan={showActionsCol ? 6 : 5}
                      style={{
                        textAlign: "center",
                        padding: 32,
                        color: "var(--text3)",
                      }}
                    >
                      <span className="spin-icon" style={{ marginRight: 8 }} />
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={showActionsCol ? 6 : 5}>
                      <div className="empty">
                        <div className="empty-ico"></div>
                        <div>No asset types found</div>
                        {canCreateAssets && (
                          <button
                            className="btn btn-p btn-sm"
                            style={{ marginTop: 10 }}
                            onClick={() => setModal({ type: "create" })}
                          >
                            Create First Type
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                {!loading &&
                  filtered.map((t) => (
                    <tr key={t.asset_type_id || t.id || t.type_code}>
                      <td className="mono">{t.asset_type_id || t.id || "—"}</td>
                      <td>
                        <span className="badge badge-amber">
                          {t.type_code || "—"}
                        </span>
                      </td>
                      <td style={{ color: "var(--text2)" }}>
                        {t.description || "—"}
                      </td>
                      <td className="mono">{t.organization_id || "—"}</td>
                      <td>
                        <span className="badge badge-blue">
                          {(t.properties || []).length} props
                        </span>
                      </td>
                      {showActionsCol && (
                        <td>
                          <div style={{ display: "flex", gap: 6 }}>
                            {canUpdateAssets && (
                              <button
                                className="btn btn-s btn-sm btn-pill"
                                onClick={() =>
                                  setModal({ type: "edit", data: t })
                                }
                              >
                                Edit
                              </button>
                            )}
                            {canDeleteAssets && (
                              <button
                                className="btn btn-d btn-sm btn-pill"
                                onClick={() => setConfirm({ data: t })}
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {modal?.type === "create" && canCreateAssets && (
        <AssetTypeModal
          mode="create"
          properties={[]}
          onClose={() => setModal(null)}
          onSave={handleCreate}
        />
      )}
      {modal?.type === "edit" && canUpdateAssets && (
        <AssetTypeModal
          mode="edit"
          initial={modal.data}
          properties={[]}
          onClose={() => setModal(null)}
          onSave={handleUpdate}
        />
      )}
      {confirm && canDeleteAssets && (
        <ConfirmModal
          title="Delete Asset Type"
          msg={`Permanently delete type "${confirm.data.type_code}"? Assets using this type may be affected.`}
          danger
          onConfirm={() => handleDelete(confirm.data)}
          onCancel={() => setConfirm(null)}
        />
      )}
    </>
  );
}

// ─── Properties Tab ────────────────────────────────────────────────────────────
function PropertiesTab({ toast }) {
  const [props, setProps] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await API.fetchProperties();
    setLoading(false);
    if (res) setProps(Array.isArray(res) ? res : res.properties || []);
    else toast("Failed to load properties", "danger");
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const subtitle = loading
    ? "Loading…"
    : `${props.length} definition${props.length !== 1 ? "s" : ""}`;

  return (
    <div className="panel">
      <div className="ph">
        <div>
          <div className="ph-title">Property definitions</div>
          <div className="ph-sub">{subtitle}</div>
        </div>
        <div className="rpt-topbar-actions">
          <button className="btn btn-s" onClick={load}>
            <i className="bi bi-arrow-clockwise" style={{ fontSize: 12 }}></i>{" "}
            Refresh
          </button>
        </div>
      </div>

      <div className="pb" style={{ padding: 0 }}>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Property ID</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td
                    colSpan={2}
                    style={{
                      textAlign: "center",
                      padding: 32,
                      color: "var(--text3)",
                    }}
                  >
                    <span className="spin-icon" style={{ marginRight: 8 }} />
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && props.length === 0 && (
                <tr>
                  <td colSpan={2}>
                    <div className="empty">
                      <div className="empty-ico"></div>
                      <div>No property definitions found</div>
                    </div>
                  </td>
                </tr>
              )}
              {!loading &&
                props.map((p) => (
                  <tr key={p.property_id}>
                    <td className="mono">{p.property_id}</td>
                    <td>{p.description || "—"}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function Assets() {
  const { toasts, show: toast, remove } = useToast();
  const [tab, setTab] = useState("assets");
  const [assetTypes, setAssetTypes] = useState([]);
  const { canSeePage, ready } = usePermissions();

  if (!ready) return null;

  if (!canSeePage("Inventory 1")) {
    return (
      <div className="panel" style={{ padding: 24 }}>
        <h2>Access denied</h2>
        <p className="tc3">You do not have permission to view inventory.</p>
      </div>
    );
  }

  return (
    <div className="page assets-page">
      <Toast toasts={toasts} remove={remove} />

      {tab === "assets" && (
        <AssetsTab toast={toast} assetTypes={assetTypes} properties={[]} />
      )}
    </div>
  );
}
